import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAnalytics } from "../analytics/context";
import { identifySendGridUser } from "../analytics/identifySendGridUser";
import { useSendGridRouter, useSendGridSession } from "../sendgrid-auth";
import { AccountRegion } from "../types/regionalApi";
import { AccountProfileResult, UserTypeResult } from "./context";
import { fetchAccountProfile, fetchFriendlyName, fetchTeammate, fetchUserType } from "./fetch";
import { getSessionUsername, removeSessionUsername } from "./sessionUsername";

/**
 * Account and profile queries for a user that already holds a SendGrid session. None of these
 * authenticate; they only use the session the auth providers established.
 */
export interface AccountQueries {
  /**
   * ```js
   * navigateToGuide();
   * ```
   * If a user is already logged in, this will navigate to the guide page.
   */
  navigateToGuide: () => Promise<void>;

  getAccountProfile: (accountRegion: AccountRegion) => Promise<AccountProfileResult>;
  updateFriendlyName: (friendlyName: string, accountRegion: AccountRegion) => Promise<void>;
  checkIfTeammateIsAdmin: (accountRegion: AccountRegion) => Promise<boolean>;
  getUserType: (accountRegion: AccountRegion) => Promise<UserTypeResult>;
}

/**
 * Use the `useAccountQueries` hook in your components to query and update the signed-in account.
 * Must be rendered inside UnifiedAuthProvider, which provides the SendGrid session and router this
 * hook reads, and inside AnalyticsProvider (see main.tsx). The session username is read from, and cleared in, the shared
 * session username store, so it always matches what the provider and the login pages see.
 */
export const useAccountQueries = (): AccountQueries => {
  const analytics = useAnalytics();
  const {
    makoToken,
    accountRegion,
    validateSession,
    isAuthenticated: isAuthenticatedWithSendGrid,
  } = useSendGridSession();
  const { navigate: navigateToMako } = useSendGridRouter();
  const reactRouterNavigate = useNavigate();

  const navigateToGuide = useCallback(async () => {
    if (!isAuthenticatedWithSendGrid) {
      return;
    }
    if (!(await validateSession())) {
      reactRouterNavigate("/logout");
      return;
    }
    if (accountRegion) {
      await identifySendGridUser(analytics, {
        makoAuthToken: makoToken,
        accountRegion,
        identity: { email: getSessionUsername() },
      });
    }
    removeSessionUsername();
    navigateToMako({
      setup2FARequired: false,
      passwordResetRequired: false,
      mfaSettingIsVerified: false,
      skipSecurityCheck: true,
      redirectToOAuth: false,
    });
  }, [
    analytics,
    isAuthenticatedWithSendGrid,
    validateSession,
    accountRegion,
    navigateToMako,
    reactRouterNavigate,
    makoToken,
  ]);

  const checkIfTeammateIsAdmin = useCallback(
    async (accountRegion: AccountRegion): Promise<boolean> => {
      if (!makoToken) {
        throw new Error("not authenticated");
      }
      const teammateResp = await fetchTeammate(makoToken, getSessionUsername(), accountRegion);

      if (!teammateResp.ok) {
        return false;
      }

      const teammateInfo = await teammateResp.json();
      return teammateInfo.is_admin;
    },
    [makoToken]
  );

  const getAccountProfile = useCallback(
    async (accountRegion: AccountRegion): Promise<AccountProfileResult> => {
      if (!makoToken) {
        throw new Error("not authenticated");
      }
      const response = await fetchAccountProfile(makoToken, accountRegion);

      if (!response.ok) {
        return { friendly_name: "My SendGrid account" };
      }

      return await response.json();
    },
    [makoToken]
  );

  const updateFriendlyName = useCallback(
    async (friendlyName: string, accountRegion: AccountRegion): Promise<void> => {
      if (!makoToken) {
        throw new Error("not authenticated");
      }
      await fetchFriendlyName(makoToken, friendlyName, accountRegion);

      return;
    },
    [makoToken]
  );

  const getUserType = useCallback(
    async (accountRegion: AccountRegion) => {
      if (!makoToken) {
        throw new Error("not authenticated");
      }
      const response = await fetchUserType(makoToken, accountRegion);
      return await response.json();
    },
    [makoToken]
  );

  return useMemo(
    () => ({
      navigateToGuide,
      checkIfTeammateIsAdmin,
      getAccountProfile,
      updateFriendlyName,
      getUserType,
    }),
    [navigateToGuide, checkIfTeammateIsAdmin, getAccountProfile, updateFriendlyName, getUserType]
  );
};
